import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, PieChart, Pie, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, RadialBarChart, RadialBar
} from 'recharts';
import shiftService from '../services/shiftService';
import adminService from '../services/adminService';
import authService from '../services/authService';

function Analytics() {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const isAdmin = authService.isAdmin();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = isAdmin
        ? await adminService.getAllShifts()
        : await shiftService.getShifts();
      setShifts(response.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter shifts by time range
  const getFilteredShifts = () => {
    if (timeRange === 'all') return shifts;
    const now = new Date();
    const cutoff = new Date();

    switch (timeRange) {
      case 'week':
        cutoff.setDate(now.getDate() - 7);
        break;
      case 'month':
        cutoff.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        cutoff.setMonth(now.getMonth() - 3);
        break;
      default:
        return shifts;
    }

    return shifts.filter(s => new Date(s.uploadDate) >= cutoff);
  };

  const filteredShifts = getFilteredShifts();

  if (loading) {
    return (
      <div className="app-container">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid rgba(5, 150, 105, 0.2)',
                borderTopColor: '#059669',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }}></div>
              <p style={{ color: 'var(--text-secondary)' }}>Loading analytics...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate analytics data
  const totalHours = filteredShifts.reduce((sum, s) => sum + (s.totalHours || 0), 0);
  const totalStandardHours = filteredShifts.reduce((sum, s) => sum + (s.totalStandardHours || 0), 0);
  const totalOvertimeHours = filteredShifts.reduce((sum, s) => sum + (s.totalOvertimeHours || 0), 0);
  const totalNightHours = filteredShifts.reduce((sum, s) => sum + (s.totalNightWorkHours || 0), 0);
  const totalStaff = filteredShifts.reduce((sum, s) => sum + (s.staffCount || 0), 0);
  const avgHoursPerUpload = filteredShifts.length > 0 ? totalHours / filteredShifts.length : 0;
  const avgStaffPerUpload = filteredShifts.length > 0 ? totalStaff / filteredShifts.length : 0;
  const overtimePercentage = totalHours > 0 ? (totalOvertimeHours / totalHours) * 100 : 0;

  // Hours distribution data for pie chart
  const hoursDistribution = [
    { name: 'Standard', value: Math.max(0, totalHours - totalOvertimeHours - totalNightHours), color: '#059669' },
    { name: 'Overtime', value: totalOvertimeHours, color: '#ef4444' },
    { name: 'Night Work', value: totalNightHours, color: '#0891b2' }
  ].filter(d => d.value > 0);

  // Trend data for area chart
  const trendData = [...filteredShifts].reverse().slice(-12).map((shift) => ({
    name: new Date(shift.uploadDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    hours: shift.totalHours || 0,
    overtime: shift.totalOvertimeHours || 0,
    night: shift.totalNightWorkHours || 0,
    staff: shift.staffCount || 0
  }));

  // Staff vs Hours comparison
  const comparisonData = [...filteredShifts].reverse().slice(-10).map((shift) => ({
    name: shift.fileName?.substring(0, 10) + '...',
    staff: shift.staffCount || 0,
    avgHours: shift.staffCount > 0 ? (shift.totalHours / shift.staffCount).toFixed(1) : 0
  }));

  // Performance metrics for radial chart
  const performanceData = [
    { name: 'Efficiency', value: Math.min(100, 100 - overtimePercentage), fill: '#059669' },
    { name: 'Overtime Rate', value: Math.min(100, overtimePercentage), fill: '#ef4444' },
    { name: 'Night Shift', value: Math.min(100, totalHours > 0 ? (totalNightHours / totalHours) * 100 : 0), fill: '#0891b2' }
  ];

  // Monthly aggregation
  const monthlyData = filteredShifts.reduce((acc, shift) => {
    const month = new Date(shift.uploadDate).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    if (!acc[month]) {
      acc[month] = { month, hours: 0, overtime: 0, uploads: 0, staff: 0 };
    }
    acc[month].hours += shift.totalHours || 0;
    acc[month].overtime += shift.totalOvertimeHours || 0;
    acc[month].uploads += 1;
    acc[month].staff += shift.staffCount || 0;
    return acc;
  }, {});

  const monthlyChartData = Object.values(monthlyData);

  const COLORS = ['#059669', '#ef4444', '#0891b2', '#eab308', '#8b5cf6', '#06b6d4'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(5, 150, 105, 0.2)',
          borderRadius: '12px',
          padding: '12px 16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, margin: '4px 0', fontSize: '0.9em' }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="app-container">
      <div className="container">
        {/* Header */}
        <div className="page-header" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10"></line>
                  <line x1="12" y1="20" x2="12" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="14"></line>
                </svg>
                Analytics Dashboard
              </h1>
              <p>Comprehensive insights into your shift data</p>
            </div>

            {/* Time Range Filter */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { value: 'week', label: '7 Days' },
                { value: 'month', label: '30 Days' },
                { value: 'quarter', label: '90 Days' },
                { value: 'all', label: 'All Time' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setTimeRange(option.value)}
                  className={`btn ${timeRange === option.value ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '8px 16px', fontSize: '0.85em' }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="stats-grid" style={{ marginBottom: '24px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalHours.toFixed(0)}</div>
              <div className="stat-label">Total Hours</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalOvertimeHours.toFixed(0)}</div>
              <div className="stat-label">Overtime Hours</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalNightHours.toFixed(0)}</div>
              <div className="stat-label">Night Hours</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalStaff}</div>
              <div className="stat-label">Staff Processed</div>
            </div>
          </div>
        </div>

        {/* Secondary Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '2em', fontWeight: '700', color: 'var(--primary)' }}>{filteredShifts.length}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Uploads</div>
          </div>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '2em', fontWeight: '700', color: 'var(--secondary)' }}>{avgHoursPerUpload.toFixed(0)}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Avg Hours/Upload</div>
          </div>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '2em', fontWeight: '700', color: 'var(--accent)' }}>{avgStaffPerUpload.toFixed(0)}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Avg Staff/Upload</div>
          </div>
          <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '2em', fontWeight: '700', color: overtimePercentage > 20 ? '#ef4444' : '#059669' }}>
              {overtimePercentage.toFixed(1)}%
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85em', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Overtime Rate</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {[
            { id: 'overview', label: 'Overview', icon: '📊' },
            { id: 'trends', label: 'Trends', icon: '📈' },
            { id: 'breakdown', label: 'Breakdown', icon: '🔍' },
            { id: 'reports', label: 'Reports', icon: '📋' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                border: activeTab === tab.id ? 'none' : '1px solid rgba(5, 150, 105, 0.2)',
                background: activeTab === tab.id ? 'linear-gradient(135deg, var(--primary), var(--secondary))' : 'white',
                color: activeTab === tab.id ? 'white' : 'var(--text-primary)',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.9em',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {shifts.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" style={{ margin: '0 auto 20px', opacity: 0.5 }}>
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>No Data Available</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
              Upload some shift data to see comprehensive analytics and visualizations.
            </p>
            <button onClick={() => navigate('/shifts/upload')} className="btn btn-primary">
              Upload Shift Data
            </button>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                {/* Hours Distribution */}
                <div className="card">
                  <div className="card-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path>
                      <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
                    </svg>
                    Hours Distribution
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={hoursDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {hoursDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Performance Metrics */}
                <div className="card">
                  <div className="card-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                    </svg>
                    Performance Metrics
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="80%" data={performanceData}>
                      <RadialBar dataKey="value" cornerRadius={10} />
                      <Legend iconSize={10} layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ paddingTop: '20px' }} />
                      <Tooltip />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Trends Tab */}
            {activeTab === 'trends' && (
              <div style={{ display: 'grid', gap: '24px' }}>
                {/* Hours Trend Area Chart */}
                <div className="card">
                  <div className="card-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                      <polyline points="17 6 23 6 23 12"></polyline>
                    </svg>
                    Hours Trend Over Time
                  </div>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorOvertime" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area type="monotone" dataKey="hours" stroke="#059669" fillOpacity={1} fill="url(#colorHours)" name="Total Hours" />
                      <Area type="monotone" dataKey="overtime" stroke="#ef4444" fillOpacity={1} fill="url(#colorOvertime)" name="Overtime" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Staff Trend */}
                <div className="card">
                  <div className="card-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    Staff Count Trend
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line type="monotone" dataKey="staff" stroke="#0891b2" strokeWidth={3} dot={{ fill: '#0891b2', strokeWidth: 2, r: 5 }} name="Staff Count" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Breakdown Tab */}
            {activeTab === 'breakdown' && (
              <div style={{ display: 'grid', gap: '24px' }}>
                {/* Monthly Breakdown */}
                {monthlyChartData.length > 0 && (
                  <div className="card">
                    <div className="card-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      Monthly Breakdown
                    </div>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={monthlyChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                        <XAxis dataKey="month" />
                        <YAxis yAxisId="left" orientation="left" stroke="#059669" />
                        <YAxis yAxisId="right" orientation="right" stroke="#0891b2" />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="hours" fill="#059669" name="Total Hours" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="left" dataKey="overtime" fill="#ef4444" name="Overtime" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="right" dataKey="staff" fill="#0891b2" name="Staff" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Staff vs Hours Comparison */}
                <div className="card">
                  <div className="card-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="20" x2="18" y2="10"></line>
                      <line x1="12" y1="20" x2="12" y2="4"></line>
                      <line x1="6" y1="20" x2="6" y2="14"></line>
                    </svg>
                    Average Hours per Staff by Upload
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={comparisonData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={100} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="avgHours" fill="#059669" name="Avg Hours/Staff" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Reports Tab */}
            {activeTab === 'reports' && (
              <div style={{ display: 'grid', gap: '24px' }}>
                {/* Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                  <div className="card" style={{ padding: '24px' }}>
                    <h4 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#059669' }}>📊</span> Hours Summary
                    </h4>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Standard Hours:</span>
                        <span style={{ fontWeight: '600' }}>{(totalHours - totalOvertimeHours - totalNightHours).toFixed(1)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Overtime Hours:</span>
                        <span style={{ fontWeight: '600', color: '#ef4444' }}>{totalOvertimeHours.toFixed(1)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Night Work Hours:</span>
                        <span style={{ fontWeight: '600', color: '#0891b2' }}>{totalNightHours.toFixed(1)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '12px', marginTop: '4px' }}>
                        <span style={{ fontWeight: '600' }}>Total:</span>
                        <span style={{ fontWeight: '700', color: 'var(--primary)' }}>{totalHours.toFixed(1)} hrs</span>
                      </div>
                    </div>
                  </div>

                  <div className="card" style={{ padding: '24px' }}>
                    <h4 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#0891b2' }}>👥</span> Staff Summary
                    </h4>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Total Staff Processed:</span>
                        <span style={{ fontWeight: '600' }}>{totalStaff}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Average per Upload:</span>
                        <span style={{ fontWeight: '600' }}>{avgStaffPerUpload.toFixed(1)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Total Uploads:</span>
                        <span style={{ fontWeight: '600' }}>{filteredShifts.length}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '12px', marginTop: '4px' }}>
                        <span style={{ fontWeight: '600' }}>Avg Hours/Staff:</span>
                        <span style={{ fontWeight: '700', color: 'var(--secondary)' }}>{totalStaff > 0 ? (totalHours / totalStaff).toFixed(1) : 0} hrs</span>
                      </div>
                    </div>
                  </div>

                  <div className="card" style={{ padding: '24px' }}>
                    <h4 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#eab308' }}>⚡</span> Efficiency Metrics
                    </h4>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Overtime Rate:</span>
                        <span style={{ fontWeight: '600', color: overtimePercentage > 20 ? '#ef4444' : '#059669' }}>{overtimePercentage.toFixed(1)}%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Night Shift Rate:</span>
                        <span style={{ fontWeight: '600' }}>{totalHours > 0 ? ((totalNightHours / totalHours) * 100).toFixed(1) : 0}%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Regular Hours Rate:</span>
                        <span style={{ fontWeight: '600', color: '#059669' }}>{totalHours > 0 ? (100 - overtimePercentage - ((totalNightHours / totalHours) * 100)).toFixed(1) : 0}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Uploads Table */}
                <div className="card">
                  <div className="card-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                    Recent Uploads Detail
                  </div>
                  <div className="table-wrapper" style={{ maxHeight: '400px' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>File Name</th>
                          <th>Upload Date</th>
                          <th>Staff</th>
                          <th>Total Hours</th>
                          <th>Overtime</th>
                          <th>Night Hours</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredShifts.slice(0, 15).map((shift) => (
                          <tr key={shift._id}>
                            <td style={{ fontWeight: '500' }}>{shift.fileName}</td>
                            <td>{new Date(shift.uploadDate).toLocaleDateString()}</td>
                            <td>{shift.staffCount || 0}</td>
                            <td>{shift.totalHours?.toFixed(1) || 0}</td>
                            <td style={{ color: (shift.totalOvertimeHours || 0) > 0 ? '#ef4444' : 'inherit' }}>
                              {shift.totalOvertimeHours?.toFixed(1) || 0}
                            </td>
                            <td style={{ color: '#0891b2' }}>{shift.totalNightWorkHours?.toFixed(1) || 0}</td>
                            <td>
                              <button
                                onClick={() => navigate(`/shifts/view/${shift._id}`)}
                                className="btn btn-primary btn-sm"
                                style={{ padding: '6px 12px', fontSize: '0.8em' }}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Back Button */}
        <div style={{ marginTop: '32px' }}>
          <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default Analytics;
